import { ThemeEvents } from '@theme/events';

const DATA_SCRIPT_ID = 'mehdi-ecommerce-data';
const CURRENCY = 'TRY';
const TRACKING_WARNING = 'GA4 tracking failed:';

const context = readTrackingContext();

trackInitialPageEvent();
document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

window.MehdiEcommerceTracking = {
  trackPaymentStep,
};

function trackInitialPageEvent() {
  if (context?.page_type === 'product' && context.product) {
    pushEcommerceEvent('view_item', {
      currency: context.currency || CURRENCY,
      value: toNumber(context.product.price),
      items: [context.product],
    });
  }

  if (context?.page_type === 'cart' && context.cart?.items?.length) {
    pushEcommerceEvent('view_cart', {
      currency: context.currency || CURRENCY,
      value: toNumber(context.cart.value),
      items: context.cart.items,
    });
  }
}

async function handleCartUpdate(event) {
  try {
    const detail = event.detail || {};
    const data = detail.data || {};

    if (data.didError || data.source !== 'product-form-component') return;

    const variantId = String(data.variantId || detail.sourceId || '');
    if (!/^\d+$/.test(variantId)) return;

    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
    const item = cart.items?.find((cartItem) => String(cartItem.variant_id) === variantId);
    if (!item) return;

    const quantity = toNumber(data.itemCount) || item.quantity || 1;
    const trackingItem = cartItemToTrackingItem(item, quantity);

    pushEcommerceEvent('add_to_cart', {
      currency: context?.currency || cart.currency || CURRENCY,
      value: toNumber(trackingItem.price) * quantity,
      items: [trackingItem],
    });
  } catch (trackingError) {
    console.warn(TRACKING_WARNING, trackingError);
  }
}

function trackPaymentStep(paymentType) {
  try {
    const cart = getCurrentCheckoutCart();
    if (!cart?.items?.length) return;

    const payload = {
      currency: context?.currency || cart.currency || CURRENCY,
      value: toNumber(cart.value),
      items: cart.items,
    };
    const cartKey = getCartKey(cart);

    pushOnce(`begin_checkout:${cartKey}`, 'begin_checkout', payload);
    pushOnce(`add_payment_info:${cartKey}:${paymentType}`, 'add_payment_info', {
      ...payload,
      payment_type: paymentType,
    });
  } catch (trackingError) {
    console.warn(TRACKING_WARNING, trackingError);
  }
}

function getCurrentCheckoutCart() {
  const checkoutCart = parseCheckoutCart();
  if (checkoutCart?.items?.length) {
    return {
      token: checkoutCart.token || context?.cart?.token || '',
      value: toNumber(checkoutCart.total_price) / 100,
      currency: checkoutCart.currency || context?.currency || CURRENCY,
      items: checkoutCart.items.map((item) => cartItemToTrackingItem(item, item.quantity)),
    };
  }

  return context?.cart || null;
}

function parseCheckoutCart() {
  if (!window.checkoutData?.cart) return null;

  try {
    return JSON.parse(window.checkoutData.cart);
  } catch (trackingError) {
    console.warn(TRACKING_WARNING, trackingError);
    return null;
  }
}

function cartItemToTrackingItem(item, quantity) {
  return {
    item_id: item.sku || String(item.variant_id),
    item_name: item.product_title,
    item_variant: item.variant_title,
    item_brand: item.vendor,
    item_category: item.product_type,
    price: toNumber(item.final_price) / 100,
    quantity,
  };
}

function pushOnce(key, eventName, payload) {
  if (sessionStorage.getItem(key)) return;

  pushEcommerceEvent(eventName, payload);
  sessionStorage.setItem(key, '1');
}

function pushEcommerceEvent(eventName, payload) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: payload,
    });
  } catch (trackingError) {
    console.warn(TRACKING_WARNING, trackingError);
  }
}

function readTrackingContext() {
  const script = document.getElementById(DATA_SCRIPT_ID);
  if (!script?.textContent) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (trackingError) {
    console.warn(TRACKING_WARNING, trackingError);
    return null;
  }
}

function getCartKey(cart) {
  if (cart.token) return cart.token;

  return `${cart.value}:${cart.items.map((item) => `${item.item_id}:${item.quantity}`).join('|')}`;
}

function toNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}
